import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useTranslation } from '../../hooks/useTranslation';
import { useResolvedLandingVariant } from '../../hooks/useResolvedLandingVariant';
import { getCountry, getUrlLocale } from '../../utils/translation/country';

export const PostHogPageView = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const { locale, data } = useTranslation();
  const { variant, configuredVariant } = useResolvedLandingVariant();

  /**
   * `locale` and `country` are attached to every event from init (see
   * services/posthog/init.ts). This keeps them in step when the visitor switches
   * country in-page, which updates the URL without reloading.
   *
   * The value is re-read from the URL rather than taken from `useTranslation`,
   * whose locale falls back to the app default when the URL carries no usable
   * `?lang=`. Recording that fallback would assert a country we do not actually
   * know — the mis-attribution the URL-derived value exists to avoid. Both
   * properties are set together so they can never disagree.
   */
  useEffect(() => {
    const urlLocale = getUrlLocale();
    if (urlLocale) posthog.register({ locale: urlLocale, country: getCountry(urlLocale) });
  }, [locale, posthog]);

  /**
   * Registers which landing experience the visitor saw, so every event from
   * this point on carries it — including the `$pageview` captured below.
   *
   * MUST stay declared ABOVE the capture effect: effects run in declaration
   * order within a component, and a super-property only rides on events
   * captured after it is registered.
   *
   * Waits for `data` because the no-online-link collapse in
   * useResolvedLandingVariant needs it to tell phone-and-link from phone-only;
   * registering earlier could report a variant the visitor never saw. `data` is
   * seeded synchronously (context/locale/provider.tsx), so this is satisfied on
   * the first render and costs no delay.
   */
  useEffect(() => {
    if (!data) return;
    posthog.register({ resolved_lp: variant, configured_lp: configuredVariant });
  }, [posthog, data, variant, configuredVariant]);

  /**
   * Captures a pageview on initial load and on every subsequent location change.
   *
   * Analytics run cookielessly (no cookies, no localStorage — see init.ts), so
   * there is no storage-consent requirement and therefore no consent gate: every
   * visitor is counted rather than only those who accepted a banner.
   *
   * The `resolved_lp` register above runs first (effects fire in declaration
   * order within a component), so the pageview carries which experience the
   * visitor saw. Registering it here rather than relying on LandingPage's own
   * effect is deliberate: this component is an EARLIER SIBLING of the <Outlet/>
   * that renders LandingPage, and React runs sibling effects in tree order, so
   * LandingPage's register always lost the race to this capture — which left
   * ~75% of production pageviews with no `resolved_lp`.
   */
  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: `${window.location.pathname}${window.location.search}`,
    });
  }, [location, posthog, variant, configuredVariant]);

  return null;
};
